import React, { useEffect, useMemo } from "react";
import {
  useTable,
  useSortBy,
  useFilters,
  useGlobalFilter,
  usePagination,
} from "react-table";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  Search2Icon,
  SearchIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Select,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";

import {
  FiAlertCircle,
  FiFilter,
  FiInbox,
  FiRefreshCw,
  FiSearch,
} from "react-icons/fi";
import { ExportButton } from "../components/ui/exportButton";

const ErrorState = ({ errorMessage = "Something went wrong", onRetry }) => {
  const iconColor = useColorModeValue("red.500", "red.300");
  const textColor = useColorModeValue("red.600", "red.200");
  const bgColor = useColorModeValue("red.50", "red.900");
  const borderColor = useColorModeValue("red.200", "red.700");

  return (
    <Box height="60vh" p={6} textAlign="center" mx="auto">
      <VStack
        spacing={4}
        bg={bgColor}
        borderRadius="lg"
        p={16}
        textAlign="center"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <Icon
          as={FiAlertCircle}
          w={10}
          h={10}
          color={iconColor}
          aria-label="Error icon"
        />

        <Text
          fontSize="md"
          fontWeight="medium"
          color={textColor}
          lineHeight="tall"
        >
          {errorMessage}
        </Text>

        {onRetry && (
          <Button
            leftIcon={<FiRefreshCw />}
            colorScheme="red"
            variant="outline"
            size="sm"
            onClick={onRetry}
            mt={2}
          >
            Try Again
          </Button>
        )}
      </VStack>
    </Box>
  );
};

const EmptyState = ({
  listName = "items",
  isFiltered = false,
  onClearFilters,
  customIcon,
  customMessage,
  showActionButton = true,
}) => {
  const iconColor = useColorModeValue("gray.400", "gray.500");
  const textColor = useColorModeValue("gray.600", "gray.400");
  const bgColor = useColorModeValue("gray.50", "gray.800");

  // Choose icon based on context
  const getIcon = () => {
    if (customIcon) return customIcon;
    if (isFiltered) return FiFilter;
    return FiInbox;
  };

  // Generate message based on context
  const getMessage = () => {
    if (customMessage) return customMessage;

    if (isFiltered) {
      return `No ${listName} found with this filter`;
    }

    return `No ${listName} found yet!`;
  };

  // Generate subtitle based on context
  const getSubtitle = () => {
    if (isFiltered) {
      return "Try adjusting your search or filter to find what you're looking for.";
    }
    return `Start by adding your first ${listName.toLowerCase()}.`;
  };

  return (
    <Box height="60vh" borderColor={useColorModeValue("gray.200", "gray.700")}>
      <VStack
        spacing={4}
        bg={bgColor}
        borderRadius="lg"
        textAlign="center"
        mx="auto"
        py={16}
        boxShadow="sm"
        borderWidth="1px"
      >
        <Icon
          as={getIcon()}
          w={12}
          h={12}
          color={iconColor}
          aria-label="Empty state icon"
        />

        <VStack spacing={2}>
          <Text
            fontSize="xl"
            fontWeight="semibold"
            color={textColor}
            lineHeight="shorter"
          >
            {getMessage()}
          </Text>

          <Text
            fontSize="sm"
            color={useColorModeValue("gray.500", "gray.400")}
            maxW="sm"
            lineHeight="tall"
          >
            {getSubtitle()}
          </Text>
        </VStack>

        {showActionButton && isFiltered && onClearFilters && (
          <Button
            leftIcon={<FiSearch />}
            colorScheme="blue"
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            mt={2}
          >
            Clear Filters
          </Button>
        )}
      </VStack>
    </Box>
  );
};

// Skeleton Table Component
const TableSkeleton = ({ columns, rowCount = 5, showColumnFilters = true }) => {
  // Generate skeleton rows
  const skeletonRows = Array.from({ length: rowCount }, (_, i) => i);

  return (
    <Box overflowX="auto">
      <Table variant="striped" colorScheme="gray">
        <Thead>
          <Tr>
            {columns.map((column, idx) => (
              <Th key={`skeleton-header-${idx}`}>
                <Flex alignItems="center">
                  <SkeletonText noOfLines={1} width="80px" />
                  {showColumnFilters && <SkeletonCircle size="4" ml={2} />}
                </Flex>
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {skeletonRows.map((rowIndex) => (
            <Tr key={`skeleton-row-${rowIndex}`}>
              {columns.map((column, colIndex) => (
                <Td key={`skeleton-cell-${rowIndex}-${colIndex}`}>
                  <SkeletonText
                    noOfLines={1}
                    width={`${Math.random() * 50 + 50}%`}
                  />
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

// Enhanced Skeleton with customizable loading states
const EnhancedTableSkeleton = ({
  columns,
  rowCount = 5,
  showColumnFilters = true,
  showPagination = true,
}) => {
  return (
    <Box height="60vh">
      <TableSkeleton
        columns={columns}
        rowCount={rowCount}
        showColumnFilters={showColumnFilters}
      />
      {showPagination && (
        <Flex
          justifyContent="space-between"
          alignItems="center"
          mt={4}
          wrap="wrap"
        >
          <Skeleton height="40px" width="150px" mb={{ base: 2, md: 0 }} />
          <HStack spacing={2}>
            <SkeletonCircle size="40px" />
            <Skeleton height="40px" width="100px" />
            <Skeleton height="40px" width="100px" />
            <SkeletonCircle size="40px" />
          </HStack>
        </Flex>
      )}
    </Box>
  );
};

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  setAllFilters,
  showGlobalSearch,
  customButton,
  handleExport,
  exportLoading,
}) {
  const [value, setValue] = React.useState(globalFilter);
  // const onChange = useAsyncDebounce((value) => {
  //   setGlobalFilter(value || undefined);
  //    setValue(e.target.value);
  // }, 200);

  const resetAll = () => {
    setGlobalFilter("");
    setAllFilters([]);
  };

  if (!showGlobalSearch) return null;

  return (
    <Flex mb={4} justifyContent="space-between" alignItems="center" wrap="wrap">
      <Flex flex="1" minWidth="250px" mr={2} justify={"space-between"}>
        <InputGroup width={["100%", "260px"]}>
          <InputLeftElement pointerEvents="none">
            <Search2Icon color="gray.400" />
          </InputLeftElement>

          <Input
            variant="filled"
            placeholder="Search"
            value={value || ""}
            onChange={(e) => {
              setValue(e.target.value);
              setGlobalFilter(e.target.value);
            }}
          />
        </InputGroup>
        <Flex gap={2}>
          {/* <Button
            onClick={() => {
              setGlobalFilter("");
              setValue("");
            }}
            width={{ base: "100%", md: "auto" }}
            mb={{ base: 2, md: 2 }}
            mr={2}
          >
            Reset
          </Button> */}

          <Button
            onClick={resetAll}
            colorScheme="orange"
            width={{ base: "100%", md: "auto" }}
          >
            Reset Filters
          </Button>

          {handleExport && (
            <ExportButton
              handleExport={handleExport}
              exportLoading={exportLoading}
            />
          )}
        </Flex>
      </Flex>
      {customButton && (
        <Box mt={{ base: 2, md: 2 }} width="100%">
          {customButton}
        </Box>
      )}
    </Flex>
  );
}

function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
  isLoading,
}) {
  const count = preFilteredRows.length;

  if (isLoading) {
    return <Skeleton height="32px" />;
  }

  return (
    <Input
      value={filterValue || ""}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
      placeholder={`Search ${count} records...`}
    />
  );
}

const allowedPages = [5, 10, 20, 30, 40, 50];
// Pagination Controls Component
const PaginationControls = ({
  pageIndex,
  pageSize,
  pageCount,
  totalItems,
  canPreviousPage,
  canNextPage,
  gotoPage,
  previousPage,
  nextPage,
  setPageSize,
  showPagination,
  isLoading,
  onPaginationChange,
}) => {
  if (!showPagination) return null;

  if (isLoading) {
    return (
      <Flex
        justifyContent="space-between"
        alignItems="center"
        mt={4}
        wrap="wrap"
      >
        <Skeleton height="40px" width="150px" mb={{ base: 2, md: 0 }} />
        <HStack spacing={2}>
          <SkeletonCircle size="40px" />
          <Skeleton height="40px" width="100px" />
          <Skeleton height="40px" width="100px" />
          <SkeletonCircle size="40px" />
        </HStack>
      </Flex>
    );
  }

  const handlePageChange = (newPage) => {
    gotoPage(newPage);
    if (onPaginationChange) {
      onPaginationChange(newPage + 1, pageSize);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    if (onPaginationChange) {
      onPaginationChange(1, newSize); // Reset to page 1 when changing page size
    }
  };

  const handlePrevious = () => {
    previousPage();
    if (onPaginationChange) {
      onPaginationChange(pageIndex, pageSize);
    }
  };

  const handleNext = () => {
    nextPage();
    if (onPaginationChange) {
      onPaginationChange(pageIndex + 2, pageSize);
    }
  };

  const startItem = pageIndex * pageSize + 1;
  const endItem = Math.min((pageIndex + 1) * pageSize, totalItems);

  return (
    <Flex justifyContent="space-between" alignItems="center" mt={4} wrap="wrap">
      <Box mb={{ base: 2, md: 0 }}>
        <Text fontSize="sm" color="gray.600">
          Showing {startItem} to {endItem} of {totalItems} results
        </Text>
        <Select
          value={pageSize}
          onChange={handlePageSizeChange}
          size="sm"
          width="auto"
          mt={1}
        >
          {allowedPages.map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </Select>
      </Box>

      <HStack spacing={2}>
        <IconButton
          onClick={() => handlePageChange(0)}
          isDisabled={!canPreviousPage}
          aria-label="First page"
          icon={<ArrowLeftIcon />}
          size="sm"
        />
        <IconButton
          onClick={handlePrevious}
          isDisabled={!canPreviousPage}
          aria-label="Previous page"
          icon={<ChevronLeftIcon />}
          size="sm"
        />

        <Box>
          <Text fontSize="sm" fontWeight="medium">
            Page {pageIndex + 1} of {pageCount}
          </Text>
        </Box>

        <IconButton
          onClick={handleNext}
          isDisabled={!canNextPage}
          aria-label="Next page"
          icon={<ChevronRightIcon />}
          size="sm"
        />
        <IconButton
          onClick={() => handlePageChange(pageCount - 1)}
          isDisabled={!canNextPage}
          aria-label="Last page"
          icon={<ArrowRightIcon />}
          size="sm"
        />
      </HStack>
    </Flex>
  );
};

const UniversalTable = ({
  tableName = "data",
  columns,
  data,
  showGlobalSearch = true,
  showColumnFilters = true,
  showPagination = false,
  customButton,
  isLoading = false,
  loadingRows = 5,
  error,
  onRetry,
  isFiltered = false,
  pagination = {
    page: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 1,
  },
  onPaginationChange,

  enableMultiSort = false,
  onSortChange, // Callback when sort changes
  handleExport,
  exportLoading = false,
}) => {
  // Ensure data is always an array
  const safeData = useMemo(() => data || [], [data]);

  const defaultColumn = useMemo(
    () => ({
      Filter: (props) => (
        <DefaultColumnFilter {...props} isLoading={isLoading} />
      ),
    }),
    [isLoading]
  );

  // Prepare pagination data for react-table
  const paginationData = useMemo(() => {
    return {
      pageIndex: Math.max(0, pagination.page - 1), // Convert to 0-based index
      pageSize: pagination.itemsPerPage,
      totalItems: pagination.totalItems,
    };
  }, [pagination]);

  // Decide which hooks to use based on props
  const tableHooks = [];
  if (showColumnFilters) {
    tableHooks.push(useFilters);
  }
  if (showGlobalSearch) {
    tableHooks.push(useGlobalFilter);
  }
  tableHooks.push(useSortBy);

  if (showPagination) {
    tableHooks.push(usePagination);
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    setAllFilters,
    rows,
    // Pagination specific props
    page, // Get the current page of rows
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      columns,
      data: safeData,
      defaultColumn,
      disableMultiSort: !enableMultiSort,
      manualPagination: showPagination, // Use manual pagination
      pageCount: Math.ceil(pagination.totalItems / pagination.itemsPerPage),
      initialState: {
        pageIndex: paginationData.pageIndex,
        pageSize: paginationData.pageSize,
      },
    },
    ...tableHooks
  );

  useEffect(() => {
    if (onSortChange && sortBy) {
      onSortChange(sortBy);
    }
  }, [sortBy, onSortChange]);

  // Use either paginated rows or all rows
  const rowsToDisplay = showPagination ? page : rows || [];

  // Handle loading state
  if (isLoading) {
    return (
      <Box>
        {showGlobalSearch && (
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
            setAllFilters={setAllFilters}
            showGlobalSearch={showGlobalSearch}
            customButton={customButton}
            handleExport={handleExport}
            exportLoading={exportLoading}
          />
        )}
        <EnhancedTableSkeleton
          columns={columns}
          rowCount={loadingRows}
          showColumnFilters={showColumnFilters}
          showPagination={showPagination}
        />
      </Box>
    );
  }

  // Handle error state
  if (error) {
    return <ErrorState errorMessage={error} onRetry={onRetry} />;
  }
  // Handle empty state
  if (!isLoading && (!data || data.length === 0)) {
    return <EmptyState listName={tableName} isFiltered={isFiltered} />;
  }

  return (
    <Box>
      {showGlobalSearch && (
        <GlobalFilter
          preGlobalFilteredRows={preGlobalFilteredRows}
          globalFilter={state.globalFilter}
          setGlobalFilter={setGlobalFilter}
          setAllFilters={setAllFilters}
          showGlobalSearch={showGlobalSearch}
          customButton={customButton}
          isLoading={isLoading}
          handleExport={handleExport}
          exportLoading={exportLoading}
        />
      )}
      <Box overflowX="auto" height="60vh">
        <Table {...getTableProps()} variant="striped" colorScheme="gray">
          <Thead>
            {headerGroups.map((headerGroup) => (
              <Tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <Th key={column.id} {...column.getHeaderProps()}>
                    <Flex alignItems="center">
                      <Box
                        {...column.getSortByToggleProps()}
                        cursor="pointer"
                        display="flex"
                        alignItems="center"
                      >
                        {column.render("Header")}
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <ChevronDownIcon ml={1} />
                          ) : (
                            <ChevronUpIcon ml={1} />
                          )
                        ) : null}
                      </Box>
                      {column.canFilter && showColumnFilters ? (
                        <Popover>
                          <PopoverTrigger>
                            <IconButton
                              aria-label="Filter column"
                              size="sm"
                              icon={<SearchIcon />}
                              variant="ghost"
                              ml={1}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </PopoverTrigger>
                          <PopoverContent>
                            <PopoverArrow />
                            <PopoverCloseButton />
                            <PopoverHeader>
                              Filter {column.render("Header")}
                            </PopoverHeader>
                            <PopoverBody>{column.render("Filter")}</PopoverBody>
                          </PopoverContent>
                        </Popover>
                      ) : null}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>

          <Tbody {...getTableBodyProps()}>
            {rowsToDisplay.map((row) => {
              prepareRow(row);
              return (
                <Tr key={row.id} {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <Td key={cell.column.id} {...cell.getCellProps()}>
                      {cell.render("Cell")}
                    </Td>
                  ))}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {showPagination && (
        <PaginationControls
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageCount={pageCount}
          totalItems={pagination.totalItems}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          setPageSize={setPageSize}
          showPagination={showPagination}
          isLoading={isLoading}
          onPaginationChange={onPaginationChange}
        />
      )}
    </Box>
  );
};

// Export all components for flexible usage
export { EnhancedTableSkeleton, TableSkeleton };
export default UniversalTable;
